import React, { useEffect, useMemo, useState } from 'react'
import {
  alpha,
  Badge,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Collapse,
  Divider,
  FormControl,
  IconButton,
  InputAdornment,
  ListSubheader,
  MenuItem,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import BuildIcon from '@mui/icons-material/Build'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import RemoveIcon from '@mui/icons-material/Remove'
import TuneIcon from '@mui/icons-material/Tune'
import {
  ExpenseLineItem,
  ExpenseSubcategoryDoc,
  OTHER_REPAIR_CATEGORY_KEY,
  RepairCategoryDoc,
} from '@otw/models'
import { formatPrice } from 'core/utils/inventoryUtils'
import { getRepairCategoryIcon } from './repairCategoryIcons'

const ADD_SENTINEL = '__add__'

export interface RepairCategoryPickerProps {
  selected: string[]
  onChange: (next: string[]) => void
  categories: RepairCategoryDoc[]
  subcategories: ExpenseSubcategoryDoc[]
  lineItems?: ExpenseLineItem[]
  onLineItemSubcategoryChange?: (index: number, subcategory: string | null) => void
  onLineItemUpdate?: (index: number, updates: Partial<ExpenseLineItem>) => void
  onLineItemDelete?: (index: number) => void
  onLineItemAdd?: (subcategory: string | null) => void
  onAddSubcategory?: () => void
  onManage?: () => void
}

/**
 * RepairPal-style two-level browse:
 *  - Horizontal scrollable tabs across categories (icon + label).
 *  - Full-width subcategory rows under the active tab. Click expands the row to
 *    show the items already assigned to it AND a quick-assign strip of unassigned
 *    items — one tap to move an unassigned item into the focused subcategory.
 *  - Unassigned line items appear at the bottom of the panel, always visible.
 */
export const RepairCategoryPicker: React.FC<RepairCategoryPickerProps> = ({
  selected,
  onChange,
  categories,
  subcategories,
  lineItems = [],
  onLineItemSubcategoryChange,
  onLineItemUpdate,
  onLineItemDelete,
  onLineItemAdd,
  onAddSubcategory,
  onManage,
}) => {
  const theme = useTheme()
  const selectedSet = useMemo(() => new Set(selected), [selected])

  const subcategoryToCategoryKey = useMemo(() => {
    const lookup = new Map<string, string>()
    for (const sub of subcategories) {
      lookup.set(sub.label, sub.categoryKey ?? OTHER_REPAIR_CATEGORY_KEY)
    }
    for (const item of lineItems) {
      if (item.subcategory && !lookup.has(item.subcategory)) {
        lookup.set(item.subcategory, OTHER_REPAIR_CATEGORY_KEY)
      }
    }
    return lookup
  }, [subcategories, lineItems])

  const subcategoriesByCategory = useMemo(() => {
    const map = new Map<string, string[]>()
    for (const [label, key] of subcategoryToCategoryKey) {
      const list = map.get(key) ?? []
      if (!list.includes(label)) list.push(label)
      map.set(key, list)
    }
    for (const list of map.values()) list.sort((a, b) => a.localeCompare(b))
    return map
  }, [subcategoryToCategoryKey])

  const lineItemsBySubcategory = useMemo(() => {
    const map = new Map<string, Array<{ item: ExpenseLineItem; index: number }>>()
    lineItems.forEach((item, index) => {
      if (!item.subcategory) return
      const list = map.get(item.subcategory) ?? []
      list.push({ item, index })
      map.set(item.subcategory, list)
    })
    return map
  }, [lineItems])

  const unassignedLineItems = useMemo(
    () =>
      lineItems
        .map((item, index) => ({ item, index }))
        .filter(({ item }) => !item.subcategory),
    [lineItems],
  )
  const unassignedTotal = useMemo(
    () => unassignedLineItems.reduce((sum, { item }) => sum + (item.amount ?? 0), 0),
    [unassignedLineItems],
  )

  const indicatorByCategory = useMemo(() => {
    const map = new Map<string, number>()
    for (const cat of categories) {
      const subs = subcategoriesByCategory.get(cat.key) ?? []
      const selectedCount = subs.filter(s => selectedSet.has(s)).length
      const itemCount = subs.reduce(
        (sum, s) => sum + (lineItemsBySubcategory.get(s)?.length ?? 0),
        0,
      )
      map.set(cat.key, Math.max(selectedCount, itemCount))
    }
    return map
  }, [categories, subcategoriesByCategory, lineItemsBySubcategory, selectedSet])

  const [activeTabKey, setActiveTabKey] = useState<string>('')
  useEffect(() => {
    if (categories.length === 0) return
    if (!activeTabKey || !categories.some(c => c.key === activeTabKey)) {
      const firstWithSignal = categories.find(c => (indicatorByCategory.get(c.key) ?? 0) > 0)
      setActiveTabKey((firstWithSignal ?? categories[0]).key)
    }
  }, [categories, activeTabKey, indicatorByCategory])

  /** Subcategory label currently expanded inline, if any. */
  const [expandedLabel, setExpandedLabel] = useState<string | null>(null)

  const activeCategory = categories.find(c => c.key === activeTabKey)
  const activeSubcategories = activeTabKey
    ? subcategoriesByCategory.get(activeTabKey) ?? []
    : []

  const toggleSubcategorySelection = (label: string) => {
    if (selectedSet.has(label)) {
      onChange(selected.filter(s => s !== label))
    } else {
      onChange([...selected, label])
    }
  }

  const handleRowClick = (label: string) => {
    setExpandedLabel(prev => (prev === label ? null : label))
  }

  const handleReassign = (index: number, value: string) => {
    if (value === ADD_SENTINEL) {
      onAddSubcategory?.()
      return
    }
    onLineItemSubcategoryChange?.(index, value === '' ? null : value)
  }

  const renderReassignSelect = (item: ExpenseLineItem, index: number) => (
    <FormControl size="small" sx={{ width: 180, flexShrink: 0 }}>
      <Select
        value={item.subcategory ?? ''}
        displayEmpty
        onChange={e => handleReassign(index, e.target.value as string)}
        onClick={e => e.stopPropagation()}
      >
        <MenuItem value="">
          <em>Unassigned</em>
        </MenuItem>
        {categories.flatMap(category => {
          const subs = subcategoriesByCategory.get(category.key) ?? []
          if (subs.length === 0) return []
          return [
            <ListSubheader key={`header-${category.key}`}>{category.label}</ListSubheader>,
            ...subs.map(s => (
              <MenuItem key={s} value={s}>
                {s}
              </MenuItem>
            )),
          ]
        })}
        {onAddSubcategory && [
          <Divider key="divider" />,
          <MenuItem key={ADD_SENTINEL} value={ADD_SENTINEL}>
            <AddIcon fontSize="small" sx={{ mr: 1 }} />
            Add subcategory…
          </MenuItem>,
        ]}
      </Select>
    </FormControl>
  )

  const renderLineItemRow = (
    item: ExpenseLineItem,
    index: number,
    keyPrefix: string,
  ) => {
    const editable = !!onLineItemUpdate
    return (
      <Stack
        key={`${keyPrefix}-${index}`}
        direction="row"
        alignItems="flex-start"
        spacing={1}
        sx={{ py: 0.75 }}
      >
        {editable ? (
          <TextField
            value={item.description}
            onChange={e => onLineItemUpdate?.(index, { description: e.target.value })}
            onClick={e => e.stopPropagation()}
            placeholder="(no description)"
            multiline
            size="small"
            variant="standard"
            fullWidth
            sx={{ flex: 1, minWidth: 0 }}
            InputProps={{ disableUnderline: false }}
          />
        ) : (
          <Typography
            variant="body2"
            sx={{
              flex: 1,
              minWidth: 0,
              color: 'text.secondary',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}
          >
            {item.description || '(no description)'}
          </Typography>
        )}
        {editable ? (
          <Stack
            direction="row"
            alignItems="center"
            spacing={0}
            sx={{ flexShrink: 0, width: 120 }}
            onClick={e => e.stopPropagation()}
          >
            <IconButton
              size="small"
              aria-label="Decrease quantity"
              onClick={() => {
                const current = item.quantity ?? 0
                const next = Math.round((current - 1) * 1000) / 1000
                onLineItemUpdate?.(index, { quantity: next > 0 ? next : null })
              }}
            >
              <RemoveIcon fontSize="small" />
            </IconButton>
            <TextField
              value={item.quantity ?? ''}
              onChange={e => {
                const raw = e.target.value.trim()
                if (raw === '') {
                  onLineItemUpdate?.(index, { quantity: null })
                  return
                }
                const next = Number(raw.replace(/,/g, ''))
                onLineItemUpdate?.(index, {
                  quantity: Number.isFinite(next) && next > 0 ? next : null,
                })
              }}
              placeholder="Qty"
              size="small"
              variant="standard"
              type="number"
              sx={{ width: 48 }}
              InputProps={{
                inputProps: {
                  min: 0,
                  step: 'any',
                  style: { textAlign: 'center' },
                  'aria-label': 'Quantity',
                },
              }}
            />
            <IconButton
              size="small"
              aria-label="Increase quantity"
              onClick={() => {
                const current = item.quantity ?? 0
                const next = Math.round((current + 1) * 1000) / 1000
                onLineItemUpdate?.(index, { quantity: next })
              }}
            >
              <AddIcon fontSize="small" />
            </IconButton>
          </Stack>
        ) : (
          <Typography
            variant="body2"
            sx={{ width: 48, textAlign: 'center', color: 'text.secondary', flexShrink: 0 }}
          >
            {item.quantity != null
              ? item.quantity.toLocaleString('en-US', { maximumFractionDigits: 3 })
              : '—'}
          </Typography>
        )}
        {editable ? (
          <TextField
            value={item.amount ?? ''}
            onChange={e => {
              const raw = e.target.value.trim()
              const next = raw === '' ? null : Number(raw.replace(/,/g, ''))
              onLineItemUpdate?.(index, {
                amount: Number.isFinite(next as number) ? (next as number) : null,
              })
            }}
            onClick={e => e.stopPropagation()}
            placeholder="0.00"
            size="small"
            variant="standard"
            type="number"
            sx={{ width: 100, flexShrink: 0 }}
            InputProps={{
              startAdornment: <InputAdornment position="start">$</InputAdornment>,
              inputProps: { style: { textAlign: 'right' } },
            }}
          />
        ) : (
          <Typography
            variant="body2"
            sx={{ width: 80, textAlign: 'right', color: 'text.secondary', flexShrink: 0 }}
          >
            {item.amount != null ? formatPrice(item.amount) : '—'}
          </Typography>
        )}
        {renderReassignSelect(item, index)}
        {onLineItemDelete && (
          <Tooltip title="Remove line item">
            <IconButton
              size="small"
              onClick={e => {
                e.stopPropagation()
                onLineItemDelete(index)
              }}
              sx={{ flexShrink: 0, mt: 0.25 }}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Stack>
    )
  }

  return (
    <Box
      sx={{
        p: 1.5,
        bgcolor: alpha(theme.palette.error.main, 0.04),
        borderRadius: 2,
        border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
        <BuildIcon color="error" fontSize="small" />
        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary' }}>
          Repair Categories
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        {onManage && (
          <Tooltip title="Manage categories & subcategories">
            <IconButton size="small" onClick={onManage}>
              <TuneIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Stack>

      <Tabs
        value={activeTabKey || (categories[0]?.key ?? '')}
        onChange={(_, value) => {
          setActiveTabKey(value as string)
          setExpandedLabel(null)
        }}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          minHeight: 56,
          '& .MuiTab-root': {
            minHeight: 56,
            textTransform: 'none',
            fontSize: '0.72rem',
            px: 1.25,
            py: 0.5,
            color: 'text.secondary',
          },
          '& .Mui-selected': { color: 'error.main' },
          '& .MuiTabs-indicator': { backgroundColor: theme.palette.error.main },
        }}
      >
        {categories.map(cat => {
          const indicator = indicatorByCategory.get(cat.key) ?? 0
          const { Icon } = getRepairCategoryIcon(cat.iconKey)
          return (
            <Tab
              key={cat.key}
              value={cat.key}
              icon={
                <Badge
                  badgeContent={indicator}
                  color="error"
                  invisible={indicator === 0}
                  overlap="circular"
                  sx={{ '& .MuiBadge-badge': { height: 14, minWidth: 14, fontSize: '0.6rem' } }}
                >
                  <Icon fontSize="small" />
                </Badge>
              }
              label={cat.label}
              iconPosition="top"
            />
          )
        })}
      </Tabs>

      <Box sx={{ mt: 1 }}>
        {activeCategory && activeSubcategories.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ py: 1, px: 1 }}>
            No subcategories in {activeCategory.label} yet.
          </Typography>
        )}

        <Stack spacing={0.75}>
          {activeSubcategories.map(label => {
            const isSelected = selectedSet.has(label)
            const isExpanded = expandedLabel === label
            const items = lineItemsBySubcategory.get(label) ?? []
            const total = items.reduce((s, { item }) => s + (item.amount ?? 0), 0)
            const ActiveIcon = activeCategory
              ? getRepairCategoryIcon(activeCategory.iconKey).Icon
              : BuildIcon
            return (
              <Card
                key={label}
                variant="outlined"
                sx={{
                  borderColor: isSelected
                    ? theme.palette.error.main
                    : isExpanded
                      ? theme.palette.error.light
                      : theme.palette.divider,
                  bgcolor: isSelected
                    ? alpha(theme.palette.error.main, 0.06)
                    : 'background.paper',
                  transition: 'border-color 0.15s, background-color 0.15s',
                }}
              >
                <CardActionArea onClick={() => handleRowClick(label)}>
                  <CardContent sx={{ py: 1.25, px: 1.5, '&:last-child': { pb: 1.25 } }}>
                    <Stack direction="row" alignItems="center" spacing={1.25}>
                      <Box
                        sx={{
                          color: isSelected ? 'error.main' : 'text.secondary',
                          display: 'flex',
                        }}
                      >
                        <ActiveIcon fontSize="small" />
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 700, color: 'text.primary' }}
                        >
                          {label}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: 'block', lineHeight: 1.3 }}
                        >
                          {activeCategory?.label}
                          {items.length > 0 && (
                            <>
                              {' · '}
                              {items.length} item{items.length === 1 ? '' : 's'}
                              {' · '}
                              {formatPrice(total)}
                            </>
                          )}
                        </Typography>
                      </Box>
                      <Chip
                        label={isSelected ? 'Selected' : 'Select'}
                        size="small"
                        color={isSelected ? 'error' : 'default'}
                        variant={isSelected ? 'filled' : 'outlined'}
                        clickable
                        onClick={e => {
                          e.stopPropagation()
                          toggleSubcategorySelection(label)
                        }}
                        icon={isSelected ? <CheckCircleIcon fontSize="small" /> : undefined}
                        sx={{ height: 22, flexShrink: 0 }}
                      />
                      {isExpanded ? (
                        <ExpandMoreIcon
                          fontSize="small"
                          sx={{ color: 'text.disabled', flexShrink: 0 }}
                        />
                      ) : (
                        <ChevronRightIcon
                          fontSize="small"
                          sx={{ color: 'text.disabled', flexShrink: 0 }}
                        />
                      )}
                    </Stack>
                  </CardContent>
                </CardActionArea>

                <Collapse in={isExpanded}>
                  <Box
                    sx={{
                      borderTop: 1,
                      borderColor: 'divider',
                      px: 1.5,
                      py: 1,
                      bgcolor: 'background.paper',
                    }}
                  >
                    {items.length === 0 ? (
                      <Typography variant="caption" color="text.secondary">
                        No line items assigned yet.
                      </Typography>
                    ) : (
                      items.map(({ item, index }) =>
                        renderLineItemRow(item, index, `sub-${label}`),
                      )
                    )}

                    {unassignedLineItems.length > 0 && (
                      <Box sx={{ mt: 1 }}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: 'block', mb: 0.5 }}
                        >
                          Quick-assign unassigned items:
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {unassignedLineItems.map(({ item, index }) => (
                            <Chip
                              key={`qa-${index}`}
                              label={`${item.description || '(no description)'}${item.amount != null ? ` · ${formatPrice(item.amount)}` : ''}`}
                              size="small"
                              onClick={() => onLineItemSubcategoryChange?.(index, label)}
                              variant="outlined"
                              icon={<AddIcon fontSize="small" />}
                              sx={{
                                cursor: 'pointer',
                                height: 'auto',
                                py: 0.5,
                                '& .MuiChip-label': {
                                  whiteSpace: 'normal',
                                  wordBreak: 'break-word',
                                },
                              }}
                            />
                          ))}
                        </Box>
                      </Box>
                    )}

                    {onLineItemAdd && (
                      <Box sx={{ mt: 1 }}>
                        <Button
                          size="small"
                          startIcon={<AddIcon fontSize="small" />}
                          onClick={e => {
                            e.stopPropagation()
                            onLineItemAdd(label)
                          }}
                          sx={{
                            textTransform: 'none',
                            color: 'text.secondary',
                            justifyContent: 'flex-start',
                            width: '100%',
                            border: '1px dashed',
                            borderColor: 'divider',
                            py: 0.5,
                          }}
                        >
                          Add line item to {label}
                        </Button>
                      </Box>
                    )}
                  </Box>
                </Collapse>
              </Card>
            )
          })}

          {onAddSubcategory && activeCategory && (
            <Card
              variant="outlined"
              sx={{ borderStyle: 'dashed', borderColor: 'divider' }}
            >
              <CardActionArea onClick={onAddSubcategory}>
                <CardContent sx={{ py: 1, px: 1.5, '&:last-child': { pb: 1 } }}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <AddIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                    <Typography variant="body2" color="text.secondary">
                      Add subcategory to {activeCategory.label}
                    </Typography>
                  </Stack>
                </CardContent>
              </CardActionArea>
            </Card>
          )}
        </Stack>
      </Box>

      {(unassignedLineItems.length > 0 || onLineItemAdd) && (
        <Box
          sx={{
            mt: 2,
            p: 1,
            borderRadius: 1,
            border: 1,
            borderColor: 'warning.light',
            bgcolor: alpha(theme.palette.warning.main, 0.06),
          }}
        >
          <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.5 }}>
            <AutoAwesomeIcon fontSize="small" sx={{ color: 'warning.main' }} />
            <Typography variant="caption" sx={{ fontWeight: 700 }}>
              Unassigned — {unassignedLineItems.length} item
              {unassignedLineItems.length === 1 ? '' : 's'}
            </Typography>
            <Box sx={{ flexGrow: 1 }} />
            <Typography variant="caption" color="text.secondary">
              {formatPrice(unassignedTotal)}
            </Typography>
          </Stack>
          {unassignedLineItems.map(({ item, index }) =>
            renderLineItemRow(item, index, 'unassigned'),
          )}
          {onLineItemAdd && (
            <Button
              size="small"
              startIcon={<AddIcon fontSize="small" />}
              onClick={() => onLineItemAdd(null)}
              sx={{
                textTransform: 'none',
                color: 'text.secondary',
                justifyContent: 'flex-start',
                width: '100%',
                border: '1px dashed',
                borderColor: alpha(theme.palette.warning.main, 0.5),
                py: 0.5,
                mt: 0.5,
              }}
            >
              Add custom line item
            </Button>
          )}
        </Box>
      )}
    </Box>
  )
}

export default RepairCategoryPicker
